<script setup>
// S16 — Connected Shopify integration workspace.
// The operator surface: durable state banner, mapping surface, scope/preview,
// durable sync phases, run history, and the operate commands (Sync now /
// Reconcile / Retry failed / Pause / Disconnect / Reauthorize).
//
// Durable + refresh-safe: on mount (and on refresh / re-login) it re-fetches
// the integration, its locations and its runs from the backend, and resumes the
// active run by id. No credential is ever read or shown.
import { ref, computed, onBeforeMount, onBeforeUnmount, watch } from "vue";
import { useCompanyStore } from "@/stores/company";
import { useShopifyStore } from "@/stores/shopify";
import LocationMappingStep from "./LocationMappingStep.vue";
import ScopePreviewStep from "./ScopePreviewStep.vue";
import SyncPhases from "./SyncPhases.vue";
import ShopifyRunHistory from "./ShopifyRunHistory.vue";
import ShopperReadinessSummary from "./ShopperReadinessSummary.vue";
import {
  CONNECTION_STATES,
  actionsForState,
  isActionAllowed,
  readableState,
  isTerminalPhase,
} from "@/utils/helpers/shopify";

// A workspace refreshes only its own active run. This keeps progress visible
// without turning every connected integration into a permanent polling client.
const ACTIVE_RUN_POLL_INTERVAL_MS = 5000;

const props = defineProps({
  integrationId: { type: String, required: true },
  // On refresh, the parent may pass the durable run id to resume.
  resumeRunId: { type: String, default: null },
});
const emit = defineEmits(["changed", "disconnected"]);

const shopifyStore = useShopifyStore();
const company_id = useCompanyStore().companyId;

const confirmDisconnect = ref(false);
const showAckOmitted = ref(false);
const ackOmitted = ref([]);
const mappingSection = ref(null);

const integration = computed(() => shopifyStore.current);
const state = computed(() => integration.value?.readiness?.state || integration.value?.state || CONNECTION_STATES.NOT_CONNECTED);
const activeRun = computed(() => shopifyStore.activeRun);
const commandLoading = computed(() => shopifyStore.commandLoading);
const allowedActions = computed(() => actionsForState(state.value));
const shouldPollActiveRun = computed(() => {
  const run = activeRun.value;
  return Boolean(
    run &&
      ["queued", "running"].includes(run.status) &&
      !isTerminalPhase(run.phase)
  );
});

let activeRunPoll = null;
const refreshedTerminalRuns = new Set();

const showMapping = computed(() =>
  [
    CONNECTION_STATES.CONNECTED_MAPPING_REQUIRED,
    CONNECTION_STATES.READY,
    CONNECTION_STATES.PARTIALLY_READY,
    CONNECTION_STATES.FAILED,
  ].includes(state.value)
);
const showScope = computed(() =>
  state.value !== CONNECTION_STATES.NOT_CONNECTED &&
  state.value !== CONNECTION_STATES.AUTHORIZING &&
  state.value !== CONNECTION_STATES.DISCONNECTED
);
const pendingLocations = computed(() => shopifyStore.pendingActiveLocations());
const mappedSiteCount = computed(() => integration.value?.mapped_locations_count || 0);
const siteReadiness = computed(() => integration.value?.site_readiness || []);
const primaryAction = computed(() => {
  if (commandLoading.value) return null;

  if (state.value === CONNECTION_STATES.REAUTHORIZATION_REQUIRED && isActionAllowed(state.value, "reauthorize")) {
    return { action: "reauthorize", label: "Reauthorize Shopify", detail: "Restore read access to continue syncing." };
  }
  if (activeRun.value && shouldPollActiveRun.value) {
    return { action: "refresh", label: "Catalogue update in progress", detail: "Shoppers continue using the last successful catalogue." };
  }
  if (pendingLocations.value.length > 0) {
    return { action: "review_mappings", label: "Review location pairings", detail: "Choose which Ravo Sites receive this catalogue." };
  }
  if (!shopifyStore.locations.length && isActionAllowed(state.value, "discover")) {
    return { action: "discover", label: "Discover locations", detail: "Find locations before choosing Ravo Site pairings." };
  }
  if (mappedSiteCount.value > 0 && isActionAllowed(state.value, "sync")) {
    return { action: "sync", label: "Start catalogue sync", detail: "Sync the catalogue into your paired Ravo Sites." };
  }
  if (isActionAllowed(state.value, "retry")) {
    return { action: "retry", label: "Retry failed sync", detail: "Retry the most recent failed catalogue run." };
  }
  if (isActionAllowed(state.value, "reconcile")) {
    return { action: "reconcile", label: "Reconcile catalogue", detail: "Check the provider against the last successful catalogue." };
  }
  return null;
});

onBeforeMount(loadAll);

async function loadAll() {
  await shopifyStore.fetchIntegration(company_id, props.integrationId).catch(() => {});
  await shopifyStore.fetchLocations(company_id, props.integrationId).catch(() => {});
  await shopifyStore.fetchRuns(company_id, props.integrationId, { limit: 20 }).catch(() => {});
  // Refresh-during-sync resume: read the durable run by id.
  const runId = props.resumeRunId || shopifyStore.runs?.[0]?.id;
  if (runId) {
    await shopifyStore.fetchRun(company_id, props.integrationId, runId).catch(() => {});
  }
}

function can(action) {
  return isActionAllowed(state.value, action) && !commandLoading.value;
}

async function onSyncNow() {
  if (pendingLocations.value.length > 0) {
    showAckOmitted.value = true;
    return;
  }

  try {
    await shopifyStore.startSync(company_id, props.integrationId, {
      mappedOnly: false,
      acknowledgedOmitted: [],
    });
    emit("changed");
  } catch (e) {
    if (e?.status === 409 && e?.data?.error_code === "mapping_incomplete") {
      showAckOmitted.value = true;
    }
  }
}

async function refreshWorkspaceState() {
  await Promise.all([
    shopifyStore.fetchIntegration(company_id, props.integrationId).catch(() => {}),
    shopifyStore.fetchLocations(company_id, props.integrationId).catch(() => {}),
    shopifyStore.fetchRuns(company_id, props.integrationId, { limit: 20 }).catch(() => {}),
  ]);
}

async function onDiscoverLocations() {
  await shopifyStore.discoverLocations(company_id, props.integrationId).catch(() => {});
  emit("changed");
}

async function onReconcile() {
  await shopifyStore.reconcile(company_id, props.integrationId).catch(() => {});
  emit("changed");
}

async function onRetry() {
  const runId = activeRun.value?.id || shopifyStore.runs?.[0]?.id;
  if (!runId) return;
  await shopifyStore.retryRun(company_id, props.integrationId, runId).catch(() => {});
  emit("changed");
}

async function onPause() {
  await shopifyStore.pause(company_id, props.integrationId).catch(() => {});
  emit("changed");
}

async function onDisconnect() {
  if (!confirmDisconnect.value) {
    confirmDisconnect.value = true;
    return;
  }
  try {
    await shopifyStore.disconnect(company_id, props.integrationId);
    confirmDisconnect.value = false;
    emit("disconnected");
  } catch (e) {
    confirmDisconnect.value = false;
  }
}

// The dashboard return target Shopify bounces back to after the server-side callback
// (mirrors ConnectShopifyModal's buildReturnUrl). Without this, the signed OAuth state
// carries no return_url and the callback falls back to a raw JSON response instead of
// reopening the workspace.
function buildReturnUrl() {
  if (typeof window === "undefined" || !window.location) return undefined;
  return `${window.location.origin}${window.location.pathname}`;
}

async function onReauthorize() {
  // The install endpoint re-finds the integration by (company, shop) and requires
  // `shop` — without it ai-back rejects with a validation error instead of minting a
  // consent URL. The operator already supplied their shop domain during the original
  // connect; reuse it here instead of prompting them to retype it.
  const res = await shopifyStore
    .reauthorize(company_id, props.integrationId, integration.value?.shop_domain, buildReturnUrl())
    .catch(() => null);
  // S16 R12: REAL redirect to Shopify's consent screen for a fresh grant.
  const url = res?.install_url || res?.data?.install_url;
  if (url && typeof window !== "undefined" && window.location) {
    window.location.assign(url);
    return;
  }
  emit("changed", res);
}

function onReviewMappings() {
  mappingSection.value?.scrollIntoView?.({ behavior: "smooth", block: "start" });
}

async function onPrimaryAction() {
  switch (primaryAction.value?.action) {
    case "reauthorize":
      await onReauthorize();
      break;
    case "refresh":
      await refreshRun();
      break;
    case "review_mappings":
      onReviewMappings();
      break;
    case "discover":
      await onDiscoverLocations();
      break;
    case "sync":
      await onSyncNow();
      break;
    case "retry":
      await onRetry();
      break;
    case "reconcile":
      await onReconcile();
      break;
  }
}

async function refreshRun() {
  const runId = activeRun.value?.id || shopifyStore.runs?.[0]?.id;
  if (!runId) return;

  const run = await shopifyStore.fetchRun(company_id, props.integrationId, runId).catch(() => null);
  // Discovery and mapping are deliberately separate operations. When discovery
  // completes, refresh the durable location list and summary rather than leaving
  // the operator to close/reopen the workspace before pairing Sites.
  if (run && isTerminalPhase(run.phase) && !refreshedTerminalRuns.has(run.id)) {
    refreshedTerminalRuns.add(run.id);
    await refreshWorkspaceState();
    emit("changed");
  }
}

function stopActiveRunPolling() {
  if (activeRunPoll) {
    clearInterval(activeRunPoll);
    activeRunPoll = null;
  }
}

async function onSyncMappedSitesOnly() {
  if (pendingLocations.value.length === 0 || mappedSiteCount.value < 1) return;

  // This acknowledgement is run-scoped. It never creates or ignores a Ravo Site;
  // the server checks this exact active-pending list again when it admits the run.
  ackOmitted.value = shopifyStore.pendingLocationRecords().map((location) => location.id);
  try {
    await shopifyStore.startSync(company_id, props.integrationId, {
      mappedOnly: true,
      acknowledgedOmitted: ackOmitted.value,
    });
    showAckOmitted.value = false;
    emit("changed");
  } catch (e) {
    // A new discovery/mapping change can make the acknowledgement stale. Keep the
    // confirmation visible so the operator reloads the durable location list.
    showAckOmitted.value = true;
  }
}

function startActiveRunPolling() {
  if (activeRunPoll || !shouldPollActiveRun.value) return;
  activeRunPoll = setInterval(() => {
    if (!shouldPollActiveRun.value) {
      stopActiveRunPolling();
      return;
    }
    refreshRun();
  }, ACTIVE_RUN_POLL_INTERVAL_MS);
}

watch(
  shouldPollActiveRun,
  (shouldPoll) => {
    if (shouldPoll) startActiveRunPolling();
    else stopActiveRunPolling();
  },
  { immediate: true }
);

onBeforeUnmount(stopActiveRunPolling);

defineExpose({
  state,
  allowedActions,
  can,
  onSyncNow,
  onSyncMappedSitesOnly,
  onDiscoverLocations,
  onReconcile,
  onRetry,
  onPause,
  onDisconnect,
  onReauthorize,
  refreshRun,
  shouldPollActiveRun,
  siteReadiness,
  primaryAction,
  onPrimaryAction,
  loadAll,
  confirmDisconnect,
});
</script>

<template>
  <div class="ws" data-test="shopify-workspace">
    <header class="ws__head">
      <div>
        <h2 class="ws__title">{{ integration && integration.display_name || "Shopify" }}</h2>
        <span class="ws__state" :data-state="state" data-test="ws-state">
          {{ readableState(state) }}
        </span>
      </div>
      <div class="ws__stats" v-if="integration">
        <div><b>{{ integration.mapped_locations_count || 0 }}</b><span>Mapped</span></div>
      </div>
    </header>

    <!-- Reauthorization / disconnected banners -->
    <div
      v-if="state === CONNECTION_STATES.REAUTHORIZATION_REQUIRED"
      class="ws__banner ws__banner--warn"
      role="alert"
      data-test="reauth-banner"
    >
      Access was revoked or scopes changed. Reauthorize to resume syncing.
    </div>
    <div
      v-else-if="state === CONNECTION_STATES.DISCONNECTED"
      class="ws__banner"
      role="alert"
      data-test="disconnected-banner"
    >
      This integration is disconnected. Your last-good catalogue is retained.
    </div>

    <section v-if="primaryAction" class="ws__next" data-test="ws-primary-action">
      <div>
        <strong>{{ primaryAction.label }}</strong>
        <p>{{ primaryAction.detail }}</p>
      </div>
      <button
        type="button"
        class="btn btn-primary btn-sm"
        :disabled="primaryAction.action === 'refresh'"
        @click="onPrimaryAction"
        :data-test="`primary-${primaryAction.action}`"
      >
        {{ primaryAction.action === "refresh" ? "Updating" : primaryAction.label }}
      </button>
    </section>

    <!-- Operate commands: every command is always rendered; disabled when not
         allowed for the current state (never silently hidden). -->
    <div class="ws__commands" data-test="ws-commands">
      <button
        type="button" class="btn btn-secondary btn-sm"
        :disabled="!can('discover')" @click="onDiscoverLocations" data-test="cmd-discover"
      >
        Discover locations
        <span v-if="commandLoading === 'discover'" class="spinner-sm" role="status" aria-hidden="true"></span>
      </button>
      <button
        type="button" class="btn btn-primary btn-sm"
        :disabled="!can('sync')" @click="onSyncNow" data-test="cmd-sync"
      >
        Sync now
        <span v-if="commandLoading === 'sync'" class="spinner-sm" role="status" aria-hidden="true"></span>
      </button>
      <button
        type="button" class="btn btn-secondary btn-sm"
        :disabled="!can('reconcile')" @click="onReconcile" data-test="cmd-reconcile"
      >
        Reconcile
        <span v-if="commandLoading === 'reconcile'" class="spinner-sm" role="status" aria-hidden="true"></span>
      </button>
      <button
        type="button" class="btn btn-secondary btn-sm"
        :disabled="!can('retry')" @click="onRetry" data-test="cmd-retry"
      >
        Retry failed
        <span v-if="commandLoading === 'retry'" class="spinner-sm" role="status" aria-hidden="true"></span>
      </button>
      <button
        type="button" class="btn btn-secondary btn-sm"
        :disabled="!can('pause')" @click="onPause" data-test="cmd-pause"
      >
        Pause
        <span v-if="commandLoading === 'pause'" class="spinner-sm" role="status" aria-hidden="true"></span>
      </button>
      <button
        type="button" class="btn btn-secondary btn-sm"
        :disabled="!can('reauthorize')" @click="onReauthorize" data-test="cmd-reauth"
      >
        Reauthorize
      </button>
      <button
        type="button" class="btn btn-danger btn-sm"
        :disabled="!can('disconnect')" @click="onDisconnect" data-test="cmd-disconnect"
      >
        {{ confirmDisconnect ? "Confirm disconnect" : "Disconnect" }}
        <span v-if="commandLoading === 'disconnect'" class="spinner-sm" role="status" aria-hidden="true"></span>
      </button>
    </div>

    <!-- mapping_incomplete acknowledgement -->
    <div v-if="showAckOmitted" class="ws__banner ws__banner--warn" data-test="ack-omitted">
      <p>
        {{ pendingLocations.length }} active provider location(s) are still awaiting a Ravo Site mapping.
        They will not be created, ignored, or synced by this choice.
      </p>
      <button
        type="button"
        class="btn btn-secondary btn-sm"
        :disabled="mappedSiteCount < 1 || !can('sync')"
        @click="onSyncMappedSitesOnly"
        data-test="cmd-sync-mapped-only"
      >
        Sync {{ mappedSiteCount }} mapped Ravo Site<span v-if="mappedSiteCount !== 1">s</span> only
      </button>
      <p v-if="mappedSiteCount < 1" class="mt-1 mb-0">
        Map at least one provider location to an existing Ravo Site before starting a partial sync.
      </p>
    </div>

    <!-- durable run phases -->
    <section v-if="activeRun" class="ws__section">
      <SyncPhases :run="activeRun" />
    </section>

    <section v-if="siteReadiness.length" class="ws__section">
      <ShopperReadinessSummary :sites="siteReadiness" />
    </section>

    <!-- mapping surface (sole editable Location->Site writer) -->
    <section v-if="showMapping" ref="mappingSection" class="ws__section">
      <LocationMappingStep
        :integration-id="props.integrationId"
        @mapped="emit('changed')"
        @conflict="emit('changed')"
      />
    </section>

    <!-- scope + preview -->
    <section v-if="showScope" class="ws__section">
      <ScopePreviewStep
        :integration-id="props.integrationId"
        :projection-settings="integration?.projection_settings"
      />
    </section>

    <!-- run history -->
    <section class="ws__section">
      <ShopifyRunHistory :integration-id="props.integrationId" />
    </section>
  </div>
</template>

<style lang="scss" scoped>
.ws { display: flex; flex-direction: column; gap: 20px; }
.ws__head { display: flex; justify-content: space-between; align-items: flex-start; }
.ws__title { font-size: 20px; font-weight: 800; margin: 0; }
.ws__state {
  display: inline-block; margin-top: 6px; font-size: 12px; font-weight: 700;
  padding: 3px 10px; border-radius: 100px; background: var(--neutral-100); color: var(--neutral-700);
}
.ws__state[data-state="ready"] { background: var(--green-50, #f0fdf4); color: var(--green-700, #15803d); }
.ws__state[data-state="failed"] { background: var(--red-50, #fef2f2); color: var(--red-700, #b91c1c); }
.ws__state[data-state="partially_ready"], .ws__state[data-state="reauthorization_required"] {
  background: var(--amber-100, #fef3c7); color: var(--amber-700, #b45309);
}
.ws__stats { display: flex; gap: 12px; }
.ws__stats b { font-size: 20px; display: block; }
.ws__stats span { font-size: 10px; text-transform: uppercase; color: var(--neutral-500); }
.ws__banner {
  border-radius: 8px; padding: 10px 14px; font-size: var(--text-sm);
  background: var(--neutral-100); color: var(--neutral-700);
}
.ws__banner--warn { background: var(--amber-100, #fef3c7); color: var(--amber-800, #92400e); }
.ws__next {
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  border: 1px solid var(--primary-200, #bfdbfe); border-radius: 8px; padding: 12px;
  background: var(--primary-50, #eff6ff);
}
.ws__next strong { display: block; font-size: var(--text-sm); }
.ws__next p { margin: 2px 0 0; color: var(--neutral-600); font-size: 12px; }
.ws__commands { display: flex; gap: 8px; flex-wrap: wrap; }
.btn-sm { padding: 5px 14px; font-size: 13px; }
.btn-danger { background: var(--red-600, #dc2626); color: #fff; }
.ws__section { border-top: 1px solid var(--neutral-200); padding-top: 16px; }
</style>
